import classes from './WellKpiDataTable.module.css';

export const allDataColumns = [
  // Display by default
  'LAB_SPECIFIC_GRAVITY',

  'CALCULATED_SEAWATER_PERCENTAGE',
  'PREDICTED_SEAWATER_PERCENTAGE',

  'LAB_SULFATE_CONCENTRATION',
  'PREDICTED_SULPHATE_CONCENTRATION',

  'LAB_STRONTIUM_CONCENTRATION',
  'PREDICTED_STRONTIUM_CONCENTRATION',

  'SIMULATOR_CELESTITE_SI_BOTTOMHOLE',
  'PREDICTED_CELESTITE_SI_BOTTOMHOLE',

  'SIMULATOR_CELESTITE_SI_SURFACE',
  'PREDICTED_CELESTITE_SI_SURFACE',

  // 'SIMULATOR_CELESTITE_SR_BOTTOMHOLE',
  // 'PREDICTED_CELESTITE_SR_BOTTOMHOLE',
  //
  // 'SIMULATOR_CELESTITE_SR_SURFACE',
  // 'PREDICTED_CELESTITE_SR_SURFACE',

  // Hide by default
  'LAB_BARIUM_CONCENTRATION',
  'LAB_BICARBONATE_CONCENTRATION',
  'LAB_BORON_CONCENTRATION',
  'LAB_BSW',
  'LAB_CALCIUM_CONCENTRATION',
  'LAB_CALCULATED_DISSOLVED_SOLIDS',
  'LAB_CARBONATE_CONCENTRATION',
  'LAB_CHLORIDE_CONCENTRATION',
  'LAB_DISSOLVED_IRON_CONCENTRATION',
  'LAB_HYDROXIDE_CONCENTRATION',
  'LAB_MAGNESIUM_CONCENTRATION',
  'LAB_MEASURED_DISSOLVED_SOLIDS',
  'LAB_PH',
  'LAB_PHOSPHATE_CONCENTRATION',
  'LAB_POTASSIUM_CONCENTRATION',
  'LAB_RESISTIVITY',
  'LAB_SALINITY',
  'LAB_SODIUM_CONCENTRATION',
  'PREDICTED_CHLORIDE_CONCENTRATION',
  'PREDICTED_SALINITY',
  'PREDICTED_SODIUM_CONCENTRATION',
] as const;

export const uncertaintyDataColumns = [
  'PREDICTED_CHLORIDE_CONCENTRATION_UNCERTAINTY',
  'PREDICTED_SALINITY_UNCERTAINTY',
  'PREDICTED_SODIUM_CONCENTRATION_UNCERTAINTY',
  'PREDICTED_STRONTIUM_CONCENTRATION_UNCERTAINTY',
  'PREDICTED_SULPHATE_CONCENTRATION_UNCERTAINTY',
];

export const columnPrecision: Partial<Record<(typeof allDataColumns)[number], number>> = {
  LAB_SPECIFIC_GRAVITY: 3,
  SIMULATOR_CELESTITE_SI_BOTTOMHOLE: 3,
  PREDICTED_CELESTITE_SI_BOTTOMHOLE: 3,
  SIMULATOR_CELESTITE_SI_SURFACE: 3,
  PREDICTED_CELESTITE_SI_SURFACE: 3,
  SIMULATOR_CELESTITE_SR_BOTTOMHOLE: 3,
  PREDICTED_CELESTITE_SR_BOTTOMHOLE: 3,
  SIMULATOR_CELESTITE_SR_SURFACE: 3,
  PREDICTED_CELESTITE_SR_SURFACE: 3,
};

export const columnStringTitles = new Map<string, string>([
  // Lab measurements
  ['LAB_BARIUM_CONCENTRATION', 'Ba²⁺'],
  ['LAB_BICARBONATE_CONCENTRATION', 'HCO₃⁻'],
  ['LAB_BORON_CONCENTRATION', 'B'],
  ['LAB_BSW', 'BSW %'],
  ['LAB_CALCIUM_CONCENTRATION', 'Ca²⁺'],
  ['LAB_CALCULATED_DISSOLVED_SOLIDS', 'TDS'],
  ['LAB_CARBONATE_CONCENTRATION', 'CO₃²⁻'],
  ['LAB_CHLORIDE_CONCENTRATION', 'Cl⁻'],
  ['LAB_DISSOLVED_IRON_CONCENTRATION', 'Fe (Dissolved)'],
  ['LAB_HYDROXIDE_CONCENTRATION', 'OH⁻'],
  ['LAB_MAGNESIUM_CONCENTRATION', 'Mg²⁺'],
  ['LAB_MEASURED_DISSOLVED_SOLIDS', 'TDS (Meas)'],
  ['LAB_PH', 'pH'],
  ['LAB_PHOSPHATE_CONCENTRATION', 'PO₄³⁻'],
  ['LAB_POTASSIUM_CONCENTRATION', 'K⁺'],
  ['LAB_RESISTIVITY', 'Resistivity'],
  ['LAB_SALINITY', 'Salinity'],
  ['LAB_SODIUM_CONCENTRATION', 'Na⁺'],
  ['LAB_SPECIFIC_GRAVITY', 'SG'],
  ['LAB_STRONTIUM_CONCENTRATION', 'Sr²⁺'],
  ['LAB_SULFATE_CONCENTRATION', 'SO₄²⁻'],

  // Predicted values with uncertainties
  ['PREDICTED_CHLORIDE_CONCENTRATION', 'Cl⁻'],
  ['PREDICTED_CHLORIDE_CONCENTRATION_UNCERTAINTY', 'Cl⁻ ±'],
  ['PREDICTED_SALINITY', 'Salinity'],
  ['PREDICTED_SALINITY_UNCERTAINTY', 'Salinity ±'],
  ['PREDICTED_SODIUM_CONCENTRATION', 'Na⁺'],
  ['PREDICTED_SODIUM_CONCENTRATION_UNCERTAINTY', 'Na⁺ ±'],
  ['PREDICTED_STRONTIUM_CONCENTRATION', 'Sr²⁺'],
  ['PREDICTED_STRONTIUM_CONCENTRATION_UNCERTAINTY', 'Sr²⁺ ±'],
  ['PREDICTED_SULPHATE_CONCENTRATION', 'SO₄²⁻'],
  ['PREDICTED_SULPHATE_CONCENTRATION_UNCERTAINTY', 'SO₄²⁻ ±'],

  // Simulator and calculated values
  ['SIMULATOR_CELESTITE_SI_BOTTOMHOLE', 'SI BH (Sim)'],
  ['PREDICTED_CELESTITE_SI_BOTTOMHOLE', 'SI BH'],
  ['SIMULATOR_CELESTITE_SI_SURFACE', 'SI WH (Sim)'],
  ['PREDICTED_CELESTITE_SI_SURFACE', 'SI WH'],
  ['SIMULATOR_CELESTITE_SR_BOTTOMHOLE', 'SR BH (Sim)'],
  ['PREDICTED_CELESTITE_SR_BOTTOMHOLE', 'SR BH'],
  ['SIMULATOR_CELESTITE_SR_SURFACE', 'SR WH (Sim)'],
  ['PREDICTED_CELESTITE_SR_SURFACE', 'SR WH'],
  ['PREDICTED_SEAWATER_PERCENTAGE', 'SW %'],
  ['CALCULATED_SEAWATER_PERCENTAGE', 'SW %'],
] as const);

export const columnTitles = new Map<string, React.ReactNode>([
  ...columnStringTitles.entries(),

  // Simulator and calculated values
  [
    'SIMULATOR_CELESTITE_SI_BOTTOMHOLE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SI</span>
      <span className={classes.description}>BH</span>
    </div>,
  ],
  [
    'PREDICTED_CELESTITE_SI_BOTTOMHOLE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SI</span>
      <span className={classes.description}>BH</span>
    </div>,
  ],
  [
    'SIMULATOR_CELESTITE_SI_SURFACE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SI</span>
      <span className={classes.description}>WH</span>
    </div>,
  ],
  [
    'PREDICTED_CELESTITE_SI_SURFACE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SI</span>
      <span className={classes.description}>WH</span>
    </div>,
  ],
  [
    'SIMULATOR_CELESTITE_SR_BOTTOMHOLE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SR</span>
      <span className={classes.description}>BH</span>
    </div>,
  ],
  [
    'PREDICTED_CELESTITE_SR_BOTTOMHOLE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SR</span>
      <span className={classes.description}>BH</span>
    </div>,
  ],
  [
    'SIMULATOR_CELESTITE_SR_SURFACE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SR</span>
      <span className={classes.description}>WH</span>
    </div>,
  ],
  [
    'PREDICTED_CELESTITE_SR_SURFACE',
    <div className={classes.stackedTitle}>
      <span className={classes.title}>SR</span>
      <span className={classes.description}>WH</span>
    </div>,
  ],
] as const);

export type DataColumn = (typeof allDataColumns)[number];
